import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Search, Filter, Music, Users, Mail, MessageSquare, Phone, Clipboard } from 'lucide-react';
import { parseISO, isValid } from 'date-fns';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import {
  fetchTrials,
  rescheduleTrial,
  setTrialStatus,
  trialHistoryApi,
  listTrialFavorites,
  removeTrialFavorite,
  setTrialFavorite,
  type TrialHistoryItem,
  type TrialLessonDTO,
} from '../../../lib/trials';
// TrialDetailsWizardModal entfernt (nicht mehr benötigt)
import { lehrerApi, LehrerOption } from '../../../lib/unterrichtseinheiten';
import TrialWizardModal from '../../../components/trials/TrialWizardModal';
import StartCourseWizardModal from '../../../components/trials/StartCourseWizardModal';
import { getEnrollmentForTrial, type EnrollmentDTO } from '../../../lib/enrollments';
import { api } from '../../../lib/api';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
// Enrollment/StartCourse laufen jetzt über den Details-Wizard (WizardShell)
import { toast } from 'sonner';
import { useStandort } from '../../../components/layouts/use-standort';
import { getSubjectLabels } from '../../../lib/settings';
import TrialLessonGridCard from '../../../components/trials/TrialLessonGridCard';
import { MeineFavoritenButton } from '../../../components/favoriten/Favoriten';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import type { TrialFavoritEintrag } from '../../../lib/trials';
import MessageWizardModal, { type MessageRecipient } from '../../../components/communications/MessageWizardModal';
import RescheduleTrialModal from '../../../components/trials/RescheduleTrialModal';
import CancelTrialWizardModal from '../../../components/trials/CancelTrialWizardModal';
import ResendTrialConfirmationWizardModal from '../../../components/trials/ResendTrialConfirmationWizardModal';
import ConsiderUntilWizardModal from '../../../components/trials/ConsiderUntilWizardModal';
import EnrollmentWizardModal from '../../../components/enrollments/EnrollmentWizardModal';
import MarkNoOccurrenceWizardModal from '../../../components/trials/MarkNoOccurrenceWizardModal';
import MarkNotStartedWizardModal from '../../../components/trials/MarkNotStartedWizardModal';
import TrialOfferWizardModal from '../../../components/trials/TrialOfferWizardModal';
import { formatDateTime } from '../../../lib/dateFormat';
import ConfirmOrderWizardModal from '../../../components/trials/ConfirmOrderWizardModal';
import { deriveTrialLessonWorkflow } from '../../../hooks/useTrialLessonWorkflow';
const TrialLessons = () => {
  const { selectedStandortId } = useStandort();
  const subjectLabel = getSubjectLabels().singular;
  const [trialLessons, setTrialLessons] = useState<TrialLessonDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [teacherFilter, setTeacherFilter] = useState<string>('all');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  // Wer welche Probestunde vorgemerkt hat — wie bei den Anfragen (T-0014).
  const [favoritenJeProbestunde, setFavoritenJeProbestunde] = useState<Record<number, TrialFavoritEintrag[]>>({});
  const darfFavorisieren = usePermission(P.anmeldung.als_favorit_hinzufuegen);
  const darfAnmeldungLoeschen = usePermission(P.anmeldung.eintrag_loeschen);
  // T-0170: Anmeldung löschen, solange noch kein Vertragskonto läuft.
  const [anmeldungLoeschen, setAnmeldungLoeschen] = useState<EnrollmentDTO | null>(null);
  // Dialogs state
  // const [cancelDialogOpen, setCancelDialogOpen] = useState(false); // legacy, nicht genutzt
  // Details-Modal entfernt

  const [messageOpen, setMessageOpen] = useState(false);
  // „Erinnerung senden" schickt keine leere Nachricht, sondern die gepflegte
  // Erinnerungsvorlage; der allgemeine Kontakt-Knopf bleibt frei.
  const [messageTemplateKey, setMessageTemplateKey] = useState('communications.manual.person');
  const [messageTitle, setMessageTitle] = useState('Nachricht an Teilnehmer');
  // Aus welcher Probestunde heraus geschrieben wird — der Versand gehört in deren Akte.
  const [messageTrialId, setMessageTrialId] = useState<number | null>(null);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionTrial, setActionTrial] = useState<TrialLessonDTO | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [considerOpen, setConsiderOpen] = useState(false);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [noOccurrenceOpen, setNoOccurrenceOpen] = useState(false);
  const [notStartedOpen, setNotStartedOpen] = useState(false);
  const [confirmOrderOpen, setConfirmOrderOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [startCourseEnrollment, setStartCourseEnrollment] = useState<EnrollmentDTO | null>(null);
  const [openHistoryOverlay, setOpenHistoryOverlay] = useState(false);
  const [historyOverlayFor, setHistoryOverlayFor] = useState<TrialLessonDTO | null>(null);
  const [historyOverlayItems, setHistoryOverlayItems] = useState<TrialHistoryItem[]>([]);
  const [historyOverlayLoading, setHistoryOverlayLoading] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState<LehrerOption[]>([]);

  const reloadTrialData = useCallback(async () => {
    if (!selectedStandortId) return;
    const [items, teachers, favorites] = await Promise.all([
      fetchTrials(undefined, undefined, selectedStandortId),
      lehrerApi.list({ standortId: Number(selectedStandortId) }),
      listTrialFavorites(Number(selectedStandortId)).catch(() => ({ meine: [] as number[], favoriten: {} })),
    ]);
    setTrialLessons((items as unknown as TrialLessonDTO[]).map((trial) => {
      const trialId = Number.parseInt(String(trial.id), 10);
      return {
        ...trial,
        isFavorite: favorites.meine.includes(trialId),
      };
    }));
    setFavoriteIds(favorites.meine);
    setFavoritenJeProbestunde(favorites.favoriten);
    setTeacherOptions(teachers);
  }, [selectedStandortId]);

  useEffect(() => {
    const load = async () => {
      if (!selectedStandortId) return;
      setIsLoading(true);
      try {
        await reloadTrialData();
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [reloadTrialData, selectedStandortId]);

  // Details laufen über TrialDetailsWizardModal (WizardShell)
  // Helpers
  const safeParseDate = (s?: string) => {
    try {
      if (!s || typeof s !== 'string') return null;
      const d = parseISO(s);
      return isValid(d) ? d : null;
    } catch { return null; }
  };
  const safeParseDateTime = (dateStr?: string, timeStr?: string) => {
    const d = (dateStr || '').trim();
    const t = (timeStr || '').trim();
    if (!d) return null;
    // normalisiere Zeit auf HH:MM:SS
    const hhmm = t.length >= 5 ? t.slice(0,5) : '00:00';
    const iso = `${d}T${hhmm}:00`;
    return safeParseDate(iso);
  };

  const isFinalizedNotProcessable = (trial: TrialLessonDTO): boolean => {
    const state = (trial as any)?.process?.state;
    return state === 'not_started';
  };

  const loadHistoryOverlay = async (trial: TrialLessonDTO) => {
    setHistoryOverlayFor(trial);
    setOpenHistoryOverlay(true);
    setHistoryOverlayLoading(true);
    try {
      const data = await trialHistoryApi.list(trial.id);
      setHistoryOverlayItems(data);
    } catch (e: any) {
      toast.error(e?.message || 'Historie konnte nicht geladen werden');
      setHistoryOverlayItems([]);
    } finally {
      setHistoryOverlayLoading(false);
    }
  };

  const appendTrialHistory = useCallback(async (trial: TrialLessonDTO, type: string, description: string) => {
    const subId = String((trial as any).subprofileId || '').trim();
    let wpUserId: number | null = null;
    let subIndex: number | null = null;
    if (subId.includes('-')) {
      const [wp, idx] = subId.split('-', 2);
      const parsedWp = Number.parseInt(wp, 10);
      const parsedIdx = idx ? Number.parseInt(idx, 10) : NaN;
      wpUserId = Number.isNaN(parsedWp) ? null : parsedWp;
      subIndex = Number.isNaN(parsedIdx) ? null : parsedIdx;
    }

    await trialHistoryApi.create(trial.id, {
      type,
      description,
      wpUserId,
      subIndex,
    });

    const createdAt = new Date().toISOString();
    setTrialLessons((prev) =>
      prev.map((item) =>
        String(item.id) === String(trial.id)
          ? {
              ...item,
              latestHistoryDescription: description,
              latestHistoryCreatedAt: createdAt,
            }
          : item
      )
    );

    if (historyOverlayFor?.id === trial.id) {
      try {
        const fresh = await trialHistoryApi.list(trial.id);
        setHistoryOverlayItems(fresh);
      } catch {
        // Overlay-Refresh ist best-effort.
      }
    }
  }, [historyOverlayFor?.id]);

  const iconForType = (t: string) => {
    const key = (t || '').toLowerCase();
    if (key.includes('email') || key === 'mail') return <Mail className="h-4 w-4" />;
    if (key.includes('whatsapp') || key === 'wa') return <MessageSquare className="h-4 w-4" />;
    if (key.includes('sms')) return <MessageSquare className="h-4 w-4" />;
    if (key.includes('call')) return <Phone className="h-4 w-4" />;
    return <Clipboard className="h-4 w-4" />;
  };

  // Filter trial lessons based on search term, status, and teacher
  const filteredTrials = trialLessons.filter(trial => {
    const q = searchTerm.toLowerCase();
    const workflow = deriveTrialLessonWorkflow(trial);
    const trialId = Number.parseInt(String(trial.id), 10);
    const isFavorite = favoriteIds.includes(trialId) || !!trial.isFavorite;
    const matchesSearch =
      searchTerm === '' ||
      (trial.studentName || '').toLowerCase().includes(q) ||
      (trial.teacherName || '').toLowerCase().includes(q) ||
      (trial.instrument || '').toLowerCase().includes(q) ||
      (trial.fachName || '').toLowerCase().includes(q);
    // Abgesagte Probestunden stehen nur unter „Abgesagt“, nicht in den Phasen (T-0168),
    // gestartete nur unter „Gestartet“ — sie sind erledigt und blieben sonst stehen (T-0290).
    const abgesagt = trial.status === 'cancelled';
    const gestartet = trial.status === 'started';
    const matchesStatus = statusFilter === 'abgesagt'
      ? abgesagt
      : statusFilter === 'gestartet'
        ? gestartet
        : !abgesagt && !gestartet && (statusFilter === 'all' || workflow.state === statusFilter);
    const matchesTeacher = teacherFilter === 'all' || trial.teacherId === teacherFilter;
    const matchesFavorites = !favoritesOnly || isFavorite;
    return matchesSearch && matchesStatus && matchesTeacher && matchesFavorites;
  });
  // Sortiert nach Datum/Zeit: neueste Termine zuerst
  const sortedTrials = [...filteredTrials].sort((a, b) => {
    const dateA = safeParseDateTime(a.date, a.startTime) || new Date(0);
    const dateB = safeParseDateTime(b.date, b.startTime) || new Date(0);
    return dateB.getTime() - dateA.getTime();
  });
  // Kontakt/Absage/Verschieben laufen über Details-Wizard (WizardShell)
  // Render cancel dialog
  // alter Cancel-Dialog entfernt – Wizard ersetzt ihn
  // Legacy-Dialogs entfernt: Details laufen über WizardShell
  // Create wizard (Grundgerüst)
  const renderCreateDialog = () => {
    const defaultTeacher = teacherFilter !== 'all' ? (typeof teacherFilter === 'string' ? Number(teacherFilter) : teacherFilter) : undefined;
    return (
      <TrialWizardModal
        key={`trial-wizard-${teacherFilter}`}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultTeacherId={defaultTeacher}
        onCreated={(created) => {
          setTrialLessons(prev => [created as unknown as TrialLessonDTO, ...prev]);
          setCreateDialogOpen(false);
        }}
      />
    );
  };
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Anmeldung &amp; Start</h1>
        <p className="text-muted-foreground">
          Verwalten Sie Probestunden, Anmeldungen und Unterrichtsstart
        </p>
      </div>
      {/* Suche links, „Meine Favoriten“ direkt daneben — wie bei den Anfragen (T-0014). */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input type="search" placeholder="Suchen..." className="pl-8 md:w-52 lg:w-64" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          {darfFavorisieren ? (
            <MeineFavoritenButton aktiv={favoritesOnly} onChange={setFavoritesOnly} />
          ) : null}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <div className="flex items-center">
                <Filter className="mr-2 h-4 w-4" />
                <span>Status</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="probestunde">Probestunde</SelectItem>
              <SelectItem value="entscheidung">Entscheidung</SelectItem>
              <SelectItem value="anmeldung">Anmeldung</SelectItem>
              <SelectItem value="angebot">Angebot</SelectItem>
              <SelectItem value="unterricht">Unterricht</SelectItem>
              <SelectItem value="gestartet">Gestartet</SelectItem>
              <SelectItem value="abgesagt">Abgesagt</SelectItem>
            </SelectContent>
          </Select>
          <Select value={teacherFilter} onValueChange={setTeacherFilter}>
            <SelectTrigger className="w-40">
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4" />
                <span>Lehrer</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Lehrer</SelectItem>
              {teacherOptions.map(teacher => <SelectItem key={teacher.id} value={String(teacher.id)}>
                  {teacher.vorname} {teacher.nachname}
                </SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Calendar className="mr-2 h-4 w-4" />
            Neue Probestunde
          </Button>
        </div>
      </div>
      {/* Trial lessons list */}
      {isLoading ? <Card>
          <CardHeader>
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card> : sortedTrials.length === 0 ? <Card className="p-8 text-center">
          <Music className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">
            Keine Probestunden gefunden
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Es wurden keine passenden Probestunden gefunden.
          </p>
          <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
            <Calendar className="mr-2 h-4 w-4" />
            Neue Probestunde planen
          </Button>
        </Card> : <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{sortedTrials.length} Probestunden gefunden</div>
            {sortedTrials.map((trial) => {
                const workflow = deriveTrialLessonWorkflow(trial);
                const processState = (trial as any)?.process?.state as string | undefined;
                const orderId = (trial as any)?.order?.id as number | undefined;
                const isTeachingState = workflow.state === 'unterricht';
                const canSendOffer = workflow.state === 'angebot' && !!trial.teacherId;
                const canStartCourse = isTeachingState && !isFinalizedNotProcessable(trial);
                const canConfirmTeaching =
                  isTeachingState &&
                  typeof orderId === 'number' &&
                  orderId > 0 &&
                  !isFinalizedNotProcessable(trial);
                // Auch nach der Probestunde lässt sich verschieben und absagen — der
                // Zustand „entscheidung" bietet beides unter „weitere Aktionen" an,
                // die Knöpfe waren aber ohne Handler und damit ausgegraut.
                const allowRescheduleCancel =
                  (workflow.state === 'probestunde' || workflow.state === 'entscheidung')
                  && !isFinalizedNotProcessable(trial);
                const isFavorite = favoriteIds.includes(Number.parseInt(String(trial.id), 10)) || !!trial.isFavorite;

                return (
                  <TrialLessonGridCard
                    key={trial.id}
                    trial={trial}
                    subjectLabel={subjectLabel}
                    workflow={workflow}
                    isFavorite={isFavorite}
                    favoriten={favoritenJeProbestunde[Number.parseInt(String(trial.id), 10)] ?? []}
                    onToggleFavorite={!darfFavorisieren ? undefined : async () => {
                      const trialId = Number.parseInt(String(trial.id), 10);
                      if (!trialId) return;
                      try {
                        const antwort = isFavorite
                          ? await removeTrialFavorite(trial.id)
                          : await setTrialFavorite(trial.id);
                        setFavoriteIds((prev) => antwort.ist_favorit
                          ? (prev.includes(trialId) ? prev : [...prev, trialId])
                          : prev.filter((id) => id !== trialId));
                        setFavoritenJeProbestunde((prev) => ({ ...prev, [trialId]: antwort.favoriten ?? [] }));
                        setTrialLessons((prev) => prev.map((t) => t.id === trial.id ? { ...t, isFavorite: antwort.ist_favorit } : t));
                      } catch (e: any) {
                        toast.error(e?.message || 'Favorit konnte nicht aktualisiert werden');
                      }
                    }}
                    onReschedule={
                      allowRescheduleCancel
                        ? () => {
                            setActionTrial(trial);
                            setRescheduleOpen(true);
                          }
                        : undefined
                    }
                    onMarkOccurred={
                      workflow.state === 'probestunde'
                        ? async () => {
                            try {
                              const trialDate = safeParseDateTime(trial.date, trial.endTime || trial.startTime);
                              if (trialDate && trialDate.getTime() > Date.now()) {
                                const proceed = globalThis.confirm(
                                  'Der Termin liegt noch in der Zukunft. Wirklich trotzdem als "hat stattgefunden" markieren?'
                                );
                                if (!proceed) return;
                              }
                              const updated = await setTrialStatus(trial.id, 'completed');
                              setTrialLessons((prev) => prev.map((t) => t.id === String(updated.id) ? (updated as any) : t));
                              toast.success('Probestunde wurde als durchgeführt markiert');
                            } catch (e: any) {
                              toast.error(e?.message || 'Status konnte nicht gesetzt werden');
                            }
                          }
                        : undefined
                    }
                    onCancel={
                      allowRescheduleCancel
                        ? () => {
                            setActionTrial(trial);
                            setCancelOpen(true);
                          }
                        : undefined
                    }
                    onResendConfirmation={
                      workflow.state === 'probestunde'
                        ? () => {
                            setActionTrial(trial);
                            setResendOpen(true);
                          }
                        : undefined
                    }
                    onNoOccurrence={
                      processState !== 'not_started' && workflow.state === 'entscheidung'
                        ? () => {
                            setActionTrial(trial);
                            setNoOccurrenceOpen(true);
                          }
                        : undefined
                    }
                    onConsiderUntil={
                      processState !== 'not_started' && workflow.state === 'entscheidung'
                        ? () => {
                            setActionTrial(trial);
                            setConsiderOpen(true);
                          }
                        : undefined
                    }
                    onEnrollment={
                      (workflow.state === 'entscheidung' || workflow.state === 'anmeldung' || workflow.state === 'angebot' || workflow.state === 'unterricht')
                        ? () => {
                            setActionTrial(trial);
                            setEnrollmentOpen(true);
                          }
                        : undefined
                    }
                    onCancelTeaching={
                      processState !== 'not_started' && workflow.state === 'unterricht'
                        ? () => {
                            setActionTrial(trial);
                            setNotStartedOpen(true);
                          }
                        : undefined
                    }
                    onNotStarted={
                      processState !== 'not_started' && (workflow.state === 'entscheidung' || workflow.state === 'anmeldung' || workflow.state === 'angebot')
                        ? () => {
                            setActionTrial(trial);
                            setNotStartedOpen(true);
                          }
                        : undefined
                    }
                    onDeleteEnrollment={
                      darfAnmeldungLoeschen && (workflow.state === 'anmeldung' || workflow.state === 'angebot')
                        ? async () => {
                            try {
                              const enrollment = await getEnrollmentForTrial(trial.id);
                              if (!enrollment) {
                                toast.error('Zu dieser Probestunde gibt es keine Anmeldung');
                                return;
                              }
                              setAnmeldungLoeschen(enrollment);
                            } catch (error: any) {
                              toast.error(error?.message || 'Anmeldung konnte nicht geladen werden');
                            }
                          }
                        : undefined
                    }
                    onOrderConfirm={
                      canConfirmTeaching
                        ? () => {
                            setActionTrial(trial);
                            setConfirmOrderOpen(true);
                          }
                        : undefined
                    }
                    onStartCourse={
                      canStartCourse
                        ? async () => {
                            try {
                              const enrollment = await getEnrollmentForTrial(trial.id);
                              if (!enrollment) {
                                toast.error('Bitte erst eine Anmeldung erstellen');
                                return;
                              }
                              setActionTrial(trial);
                              setStartCourseEnrollment(enrollment);
                            } catch (error: any) {
                              toast.error(error?.message || 'Anmeldung konnte nicht geladen werden');
                            }
                          }
                        : undefined
                    }
                    onSendOffer={
                      canSendOffer
                        ? () => {
                            setActionTrial(trial);
                            setOfferOpen(true);
                          }
                        : undefined
                    }
                    onReminder={() => {
                      setMessageTemplateKey('enrollment.reminder.1');
                      setMessageTitle('Erinnerung an die Anmeldung');
                      const standort = selectedStandortId ? Number(selectedStandortId) : 0;
                      if (!standort) {
                        toast.error('Kein Standort ausgewählt');
                        return;
                      }
                      const subId = String((trial as any).subprofileId || '').trim();
                      let wpUserId: number | null = null;
                      let subIndex: number | null = null;
                      if (subId.includes('-')) {
                        const [wp, idx] = subId.split('-', 2);
                        const wpParsed = Number.parseInt(wp, 10);
                        const idxParsed = idx ? Number.parseInt(idx, 10) : NaN;
                        wpUserId = Number.isNaN(wpParsed) ? null : wpParsed;
                        subIndex = Number.isNaN(idxParsed) ? null : idxParsed;
                      }
                      if (!wpUserId) {
                        toast.error('Schülerprofil-ID fehlt – kann Empfänger nicht auflösen');
                        return;
                      }
                      setMessageRecipients([
                        {
                          id: `person:${wpUserId}:${subIndex || 0}`,
                          type: 'person',
                          wpUserId,
                          subIndex,
                          name: (trial as any).studentName || 'Teilnehmer',
                          email: (trial as any).contactEmail || null,
                          phone: (trial as any).contactPhone || null,
                        },
                      ]);
                      setMessageTrialId(Number.parseInt(String(trial.id), 10) || null);
                      setMessageOpen(true);
                    }}
                    onEmail={() => {
                      setMessageTemplateKey('communications.manual.person');
                      setMessageTitle('Nachricht an Teilnehmer');
                      const standort = selectedStandortId ? Number(selectedStandortId) : 0;
                      if (!standort) {
                        toast.error('Kein Standort ausgewählt');
                        return;
                      }
                      const subId = String((trial as any).subprofileId || '').trim();
                      let wpUserId: number | null = null;
                      let subIndex: number | null = null;
                      if (subId.includes('-')) {
                        const [wp, idx] = subId.split('-', 2);
                        const wpParsed = Number.parseInt(wp, 10);
                        const idxParsed = idx ? Number.parseInt(idx, 10) : NaN;
                        wpUserId = Number.isNaN(wpParsed) ? null : wpParsed;
                        subIndex = Number.isNaN(idxParsed) ? null : idxParsed;
                      }
                      if (!wpUserId) {
                        toast.error('Schülerprofil-ID fehlt – kann Empfänger nicht auflösen');
                        return;
                      }
                      setMessageRecipients([
                        {
                          id: `person:${wpUserId}:${subIndex || 0}`,
                          type: 'person',
                          wpUserId,
                          subIndex,
                          name: (trial as any).studentName || 'Teilnehmer',
                          email: (trial as any).contactEmail || null,
                          phone: (trial as any).contactPhone || null,
                        },
                      ]);
                      setMessageTrialId(Number.parseInt(String(trial.id), 10) || null);
                      setMessageOpen(true);
                    }}
                    onContactAction={async (channel) => {
                      const label =
                        channel === 'phone'
                          ? 'Kontaktaktion: Telefon geöffnet'
                          : channel === 'call_failed'
                            ? 'Kontaktaktion: Nicht erreicht'
                          : channel === 'email'
                            ? 'Kontaktaktion: E-Mail geöffnet'
                            : 'Kontaktaktion: Nachricht geöffnet';
                      await appendTrialHistory(trial, `trial.contact.${channel}`, label);
                    }}
                    onAddQuickNote={async (note) => {
                      await appendTrialHistory(trial, 'trial.comment', note.trim());
                    }}
                    onOpenHistory={() => { void loadHistoryOverlay(trial); }}
                  />
                );
              })}
          </div>}
      {/* Dialogs */}
      {/* Alter einfacher Cancel-Dialog wurde durch Wizard ersetzt */}
      {renderCreateDialog()}

      {/* Unter-Wizards (ohne Details-Modal) */}
      {actionTrial ? (
        <>
          <RescheduleTrialModal
            open={rescheduleOpen}
            onOpenChange={(v) => {
              setRescheduleOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            onConfirm={async (payload) => {
              const updated = await rescheduleTrial(actionTrial.id, payload);
              setTrialLessons(prev => prev.map(t => t.id === String(updated.id) ? (updated as any) : t));
              toast.success('Probestunde wurde verschoben');
              setRescheduleOpen(false);
              setActionTrial(null);
            }}
          />

          <CancelTrialWizardModal
            open={cancelOpen}
            onOpenChange={(v) => {
              setCancelOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            onDone={(updated) => {
              // Den neuen Stand übernehmen statt die Karte zu entfernen — sie steht
              // danach unter „Abgesagt“ (T-0168).
              setTrialLessons(prev => prev.map(t => t.id === String(updated.id) ? ({ ...t, ...(updated as any), status: 'cancelled' }) : t));
              toast.success('Probestunde wurde abgesagt — zu finden unter „Abgesagt“');
              setCancelOpen(false);
              setActionTrial(null);
            }}
          />

          <ResendTrialConfirmationWizardModal
            open={resendOpen}
            onOpenChange={(v) => {
              setResendOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
            onUpdated={(t) => setTrialLessons(prev => prev.map(x => x.id === String(t.id) ? (t as any) : x))}
          />

          <ConsiderUntilWizardModal
            open={considerOpen}
            onOpenChange={(v) => {
              setConsiderOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
            onUpdated={(t) => setTrialLessons(prev => prev.map(x => x.id === String(t.id) ? (t as any) : x))}
          />

          <EnrollmentWizardModal
            open={enrollmentOpen}
            onOpenChange={(v) => {
              setEnrollmentOpen(v);
              if (!v) setActionTrial(null);
            }}
            trialId={actionTrial.id}
            trialData={actionTrial as any}
            subprofileId={(actionTrial as any).subprofileId}
            teacherId={actionTrial.teacherId ? Number(actionTrial.teacherId) : undefined}
            defaults={{
              weekday: ((new Date(actionTrial.date + 'T00:00:00').getDay() + 6) % 7) + 1,
              startTime: actionTrial.startTime?.slice(0, 5),
              startDate: actionTrial.date,
              intervalWeeks: 1,
            }}
            onCreated={async () => {
              toast.success('Anmeldung erstellt');
              await reloadTrialData();
            }}
          />

          <MarkNoOccurrenceWizardModal
            open={noOccurrenceOpen}
            onOpenChange={(v) => {
              setNoOccurrenceOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
            onUpdated={(t) => {
              setTrialLessons(prev => prev.map(x => x.id === String(t.id) ? (t as any) : x));
            }}
          />

          <MarkNotStartedWizardModal
            open={notStartedOpen}
            onOpenChange={(v) => {
              setNotStartedOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
            onUpdated={(t) => {
              setTrialLessons(prev => prev.map(x => x.id === String(t.id) ? (t as any) : x));
            }}
          />

          <LoeschenMitGrundDialog
            offen={anmeldungLoeschen !== null}
            titel="Anmeldung löschen"
            beschreibung={`Die Anmeldung #${anmeldungLoeschen?.id ?? ''} wird endgültig gelöscht, ein reservierter Kurs wird wieder frei. Gehört schon ein Vertragskonto dazu, geht das nicht — dann bitte kündigen.`}
            onAbbrechen={() => setAnmeldungLoeschen(null)}
            onLoeschen={async (grund) => {
              if (!anmeldungLoeschen) return;
              await api.delete(`/api/enrollments/${anmeldungLoeschen.id}`, grundHeader(grund));
              setAnmeldungLoeschen(null);
              toast.success('Anmeldung gelöscht');
              await reloadTrialData();
            }}
          />

          <ConfirmOrderWizardModal
            open={confirmOrderOpen}
            onOpenChange={(v) => {
              setConfirmOrderOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            onConfirmed={async () => {
              await reloadTrialData();
              setConfirmOrderOpen(false);
              setActionTrial(null);
            }}
          />

          <StartCourseWizardModal
            open={startCourseEnrollment !== null}
            onOpenChange={(v) => {
              if (!v) {
                setStartCourseEnrollment(null);
                setActionTrial(null);
              }
            }}
            enrollmentId={startCourseEnrollment?.id ?? 0}
            enrollment={startCourseEnrollment}
            onComplete={async () => {
              setStartCourseEnrollment(null);
              setActionTrial(null);
              await reloadTrialData();
            }}
          />

          <TrialOfferWizardModal
            open={offerOpen}
            onOpenChange={(v) => {
              setOfferOpen(v);
              if (!v) setActionTrial(null);
            }}
            trial={actionTrial}
            standortId={selectedStandortId ? Number(selectedStandortId) : undefined}
            onUpdated={async (updatedTrial) => {
              setTrialLessons((prev) => prev.map((t) => t.id === String(updatedTrial.id) ? updatedTrial : t));
              await reloadTrialData();
            }}
          />
        </>
      ) : null}

      <Dialog open={openHistoryOverlay} onOpenChange={setOpenHistoryOverlay}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Historie</DialogTitle>
            <DialogDescription>
              {historyOverlayFor?.studentName || ''}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {historyOverlayLoading ? (
              <div className="text-sm text-muted-foreground">Lade…</div>
            ) : historyOverlayItems.length === 0 ? (
              <div className="text-sm text-muted-foreground">Noch keine Historie</div>
            ) : (
              historyOverlayItems.map((h) => (
                <div key={h.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="text-muted-foreground">{iconForType(h.type)}</div>
                      <div className="font-medium text-sm">{h.description}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">{formatDateTime(h.createdAt)}</div>
                      {(h.actorName || h.actorEmail || h.actorUserId) ? (
                        <div className="text-xs text-muted-foreground">
                          {h.actorName || h.actorEmail || `User #${h.actorUserId}`}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {h.details ? (
                    <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{h.details}</div>
                  ) : null}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenHistoryOverlay(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MessageWizardModal
        open={messageOpen}
        onOpenChange={setMessageOpen}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title={messageTitle}
        recipients={messageRecipients}
        context={messageTrialId ? { entityType: 'trial', entityId: messageTrialId } : undefined}
        initialChannel="email"
        templateKeys={{
          email: messageTemplateKey,
          sms: messageTemplateKey,
        }}
        initialDrafts={
          messageTemplateKey === 'communications.manual.person'
            ? {
                email: {
                  subjectTpl: 'Nachricht von {{schoolName}}',
                  htmlTpl: '<p>Hallo {{recipientName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>',
                  textTpl: 'Hallo {{recipientName}},\n\n\nViele Grüße\n{{schoolName}}',
                },
                sms: { smsTpl: 'Hallo {{recipientName}}, ' },
              }
            // Bei der Erinnerung gilt die gepflegte Vorlage, kein Platzhaltertext.
            : undefined
        }
      />
    </div>;
};
export default TrialLessons;
